import * as React from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps } from 'theme/jss-types';
import { formFields } from 'components/create-consignment/create-modal.constants';
import { useTranslation } from 'react-i18next';
import {
    Col,
    Form,
    FormInstance,
    Input,
    Select,
} from 'antd';
import { formRules } from 'library/constants';
import { FormField, InputTypes } from 'components/create-consignment/create-consignment.types';

const styles = () => ({
    formItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
        '& .ant-checkbox + span': {
            color: '#111111',
            fontSize: 14,
            fontWeight: 600,
        },
    },
    title: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
        marginLeft: 4,
    },
    referenceTitle: {
        color: '#333333',
        fontFamily: 'Open Sans',
        fontSize: 12,
        letterSpacing: 0,
        width: '100%',
        marginBottom: 4,
    },
    formItemWeight: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'left',
    },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReturnType<typeof mapStateToProps> {
    params: any;
    form: FormInstance;
    shouldNotMakeItemTypeCheck: boolean;
    isRenderedFromLayout: boolean | undefined;
    allowedWeightUnits: string[] | undefined;
}

const WeightField = (props: IProps) => {
    const { t, i18n } = useTranslation();
    const {
        classes,
        params,
        autoCalculateDetails,
        form,
        pieceSubLayout,
        envelopeSubLayout,
        documentSubLayout,
        isRenderedFromLayout,
        allowedWeightUnits,
    } = props;
    let {
        shouldNotMakeItemTypeCheck,
    } = props;
    const {
        WeightUnit,
        ConsignmentCategory,
        Weight,
        ItemType,
    } = formFields;
    const {
        id: pieceIndex, isRequired, getValidationRules, getIsRequiredValidation, disabledFields = {},
    } = params;

    const formRuleRequired = {
        ...formRules.required,
        message: i18n.exists('required') ? t('required') : 'Required',
    };

    const consignmentCategory = Form.useWatch(ConsignmentCategory.key, form);
    const itemType = Form.useWatch(ItemType.key, form);
    const isPieceLevel = pieceIndex !== null && pieceIndex !== undefined;
    shouldNotMakeItemTypeCheck = shouldNotMakeItemTypeCheck || false;

    if (!isRenderedFromLayout) {
        if (itemType === 'document' && !documentSubLayout.includes('weight') && !isPieceLevel) {
            return <></>;
        }
        if (itemType === 'envelope' && !envelopeSubLayout.includes('weight') && !isPieceLevel) {
            return <></>;
        }
        if ((!itemType || itemType === 'non-document') && !pieceSubLayout.includes('weight') && !isPieceLevel) {
            return <></>;
        }
    }

    const shouldDisable = () => {
        if (shouldNotMakeItemTypeCheck) {
            return !isPieceLevel && consignmentCategory === 'international' && autoCalculateDetails;
        }

        return !isPieceLevel && consignmentCategory === 'international'
            && itemType === 'non-document' && autoCalculateDetails;
    };

    const renderInput = (field: FormField, type: string, fieldName: string | undefined) => {
        return (
            <Form.Item
                name={fieldName}
                initialValue={Weight.defaultValue}
                valuePropName={Weight.valuePropName}
                rules={getValidationRules(field, isPieceLevel)}
                style={{ width: '100%', flex: 1 }}
            >
                <Input
                    type="number"
                    min={0}
                    placeholder={t(Weight.key)}
                    disabled={shouldDisable()
                        || disabledFields[fieldName || '']
                        || (isPieceLevel && disabledFields.piece)
                        || (isPieceLevel && disabledFields[`piece_${field.key}`])}
                />
            </Form.Item>
        );
    };

    const renderSelect = (field: FormField, fieldName: string | undefined) => {
        if (Array.isArray(field.options) && field.options.length > 0) {
            field.options.forEach((item: any) => {
                /* eslint-disable no-param-reassign */
                item.label = i18n.exists(item.value) ? t(item.value) : item.label;
            });
        }
        const weightUnitOptions = allowedWeightUnits?.length
            ? WeightUnit.options?.filter((o: any) => allowedWeightUnits.includes(o.value))
            : WeightUnit.options;
        const weightUnitInitialValue = allowedWeightUnits?.length
            ? (weightUnitOptions?.[0]?.value ?? WeightUnit.defaultValue)
            : WeightUnit.defaultValue;
        return (
            <Form.Item
                name={fieldName}
                key={WeightUnit.key}
                initialValue={weightUnitInitialValue}
                rules={isRequired(isPieceLevel, WeightUnit.key) || getIsRequiredValidation(WeightUnit.key, isPieceLevel)
                    ? [formRuleRequired] : undefined}
                className="selectDropdown"
            >
                <Select
                    placeholder={WeightUnit.placeholder}
                    options={weightUnitOptions}
                    dropdownMatchSelectWidth={false}
                    disabled={shouldDisable()
                        || disabledFields[fieldName || '']
                        || (isPieceLevel && disabledFields.piece)
                        || (isPieceLevel && disabledFields[`piece_${field.key}`])}
                >
                    {weightUnitOptions?.map((item: any) => {
                        return (
                            <Select.Option
                                key={item.value}
                                value={item.value}
                            >
                                {item.label}
                            </Select.Option>
                        );
                    })}
                </Select>
            </Form.Item>
        );
    };

    const renderFormItem = (
        field: FormField,
        type: string = 'text',
        fieldName: string | undefined = undefined,
    ) => {
        switch (field.type) {
            case InputTypes.Input: return renderInput(field, type, fieldName);
            case InputTypes.Select: return renderSelect(field, fieldName);
            default: return 'Input is not defined';
        }
    };

    const weightKey = isPieceLevel ? `piece_${Weight.key}_${pieceIndex}` : Weight.key;
    const weightUnitKey = isPieceLevel ? `piece_${WeightUnit.key}_${pieceIndex}` : WeightUnit.key;
    return (
        <Col span={24} xs={24} sm={24} md={12}>
            <div className={classes.formItemWeight}>
                <div className={classes.title}>
                    {t(Weight.key)}
                    {isRequired(isPieceLevel, Weight.key) || getIsRequiredValidation(Weight.key, isPieceLevel) ? '*' : null}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                    {renderFormItem(Weight, 'number', weightKey)}
                    {renderFormItem(WeightUnit, 'string', weightUnitKey)}
                </div>
            </div>
        </Col>
    );
};

const mapStateToProps = (state: any) => {
    const { master } = state;
    const { config } = master;
    const customerPortalConfig = config?.customer_portal_config;
    const autoCalculateDetails = customerPortalConfig?.auto_calculate_single_cn_fields_international;
    const orderCreationConfig = config?.customer_portal_order_creation_config || {};
    const formSubLayout = orderCreationConfig?.sub_layout || {};
    const documentSubLayout = formSubLayout?.document || [];
    const pieceSubLayout = formSubLayout?.non_document || [];
    const envelopeSubLayout = formSubLayout?.envelope || [];
    const allowedWeightUnits = orderCreationConfig?.allowed_weight_units;
    return {
        autoCalculateDetails,
        pieceSubLayout,
        documentSubLayout,
        envelopeSubLayout,
        allowedWeightUnits,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
};

export default GenericHoc(hocConfig)(WeightField);
